import React, { useState } from 'react';
import { CustomOptionResolvers } from '@europrocurement/flexy-form';
import { useFormaliteService, useOffreService } from '@europrocurement/l2d-domain';
import { useNavigate, useParams } from 'react-router-dom';
import { CenterCircularProgress, Previous } from '@europrocurement/flexy-components';
import { jsonSection, type jsonForm } from '@europrocurement/flexy-form/utils/types/formTypes';
import { Backdrop, CircularProgress, Box, Container } from '@mui/material';
import { castIriToId } from '@europrocurement/l2d-utils';
import { TblRelOptionJsonldCatalogueReadCatalogueReadGrilleTarifaireCatalogueReadFull as TblRelOptionJsonldCatalogueRead } from '@europrocurement/l2d-domain/openApi/ApiOffre';
import { useFullLayout } from '@europrocurement/flexy-components/components/templates/FullLayout/FullLayoutContext';
import { enqueueSnackbar } from 'notistack';
import { useOptionComplementaire } from '@europrocurement/l2d-modules/hooks';
import { JsonForm } from '../../JsonForms';
import { useParcoursFormalite, useParcoursFormalitreHeader } from '../provider';
import { useListeSelectionDataResolver } from '../../JsonForms/hooks/useListeSelectionDataResolver';
import { updateJsonPrestationFormalite } from '../provider/functions';
import { parcoursFormalitePathResolver } from '../parcoursFormalitePathResolver';
import CurrentFormalityAndClient from '../components/currentFormalityAndClient';
import ParcoursSirenRneProvider from '../../JsonForms/components/customItems/SirenRne/ParcoursSirenRneProvider';

export type StepFormulairesProps = {
    onFinish?: (data: Record<string, unknown> | undefined) => void;
};

const StepFormulaires: React.FunctionComponent<StepFormulairesProps> = function ({ onFinish }) {
    const {
        getCurrentDossier,
        getCurrentCollection,
        getCurrentPresta,
        containerWidth,
        currentCatalog,
        currentCatalogs,
        refreshDossier,
        fetchForm,
        form,
        formLoading,
        isNextPresta,
        switchToPresta,
        setLoading,
    } = useParcoursFormalite();
    const dossier = getCurrentDossier();
    const collection = getCurrentCollection();
    const presta = getCurrentPresta();

    const { prestationFormaliteApi } = useFormaliteService();
    const navigate = useNavigate();

    const createListeSelectionDataResolver = useListeSelectionDataResolver();

    const { indexPresta: strIndexPresta, page: strpage } = useParams();

    if (strIndexPresta === undefined || strpage === undefined) {
        throw new Error('missing page');
    }

    const page = Number(strpage === undefined ? 0 : strpage);
    const indexPresta = Number(strIndexPresta === undefined ? 0 : strIndexPresta);

    React.useEffect(() => {
        setLoading(false);
    }, [setLoading]);

    const [pagelenth, setPagelenth] = useState<number | undefined>(page);

    const defaultValues = {};

    const { changeParcoursSteps, setIsParcoursPath } = useParcoursFormalitreHeader();

    const { setProfileHeaderVisible } = useFullLayout();
    React.useEffect(() => {
        changeParcoursSteps(
            2,
            pagelenth
                ? (10 + (80 / pagelenth) * page) / (currentCatalogs?.length || 1) +
                      (80 / (currentCatalogs?.length || 1)) * indexPresta
                : 10,
        );
        setProfileHeaderVisible(false);
        setIsParcoursPath(true);
        return () => {
            setIsParcoursPath(false);
            setProfileHeaderVisible(true);
        };
    }, [
        setProfileHeaderVisible,
        changeParcoursSteps,
        setIsParcoursPath,
        pagelenth,
        page,
        currentCatalogs?.length,
        indexPresta,
    ]);

    const { offerOptionsApi } = useOffreService();
    const { catalogueOptionsToFormulaire } = useOptionComplementaire();

    React.useEffect(() => {
        if (!form && dossier) {
            fetchForm();
        }
    }, [dossier, fetchForm, form]);

    const [pageOptCompl, setPageOptCompl] = useState<{
        titre: string;
        sections: jsonSection[];
    } | null | void>();

    React.useEffect(() => {
        const pageOpt = async (options: Array<TblRelOptionJsonldCatalogueRead>) => {
            const resPageOptCompl = await catalogueOptionsToFormulaire(options);
            setPageOptCompl(resPageOptCompl);
        };
        if (currentCatalog?.options) {
            pageOpt(currentCatalog.options);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCatalog, offerOptionsApi]);

    React.useEffect(() => {
        const havePresta = switchToPresta(indexPresta);
        if (!havePresta) {
            enqueueSnackbar(`Prestation introuvable (${indexPresta})`, { variant: 'error' });
        }
    }, [indexPresta, switchToPresta]);

    React.useEffect(() => {
        if (!form && !formLoading) {
            try {
                const haveNext = isNextPresta();
                if (haveNext) {
                    navigate(parcoursFormalitePathResolver('stepForm', indexPresta + 1, 0));
                } else {
                    navigate(parcoursFormalitePathResolver('stepDocs'));
                }
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
            } catch (e) {
                enqueueSnackbar(`Erreur lors de la vérification de la prochaine prestation`, {
                    variant: 'error',
                });
                navigate('/');
            }
        }
    }, [dossier, form, formLoading, indexPresta, navigate, refreshDossier, isNextPresta]);

    if (!dossier || !collection || !collection.id) {
        if (!dossier) {
            console.warn('Missing dossier');
        }
        if (!collection) {
            console.warn('Missing collection');
        }
        if (collection && !collection.id) {
            console.warn('Missing collection id');
        }

        return <CenterCircularProgress />;
    }

    let formjson;
    if (form) {
        const formjsonstring = form.formulaire;
        if (formjsonstring) {
            formjson = JSON.parse(formjsonstring);
        }
    }

    if (formjson && currentCatalog?.options) {
        if (pageOptCompl) {
            formjson.pages.push(pageOptCompl);
        }
    }

    const onPrevious = () => {
        navigate(parcoursFormalitePathResolver('stepClient'));
    };

    const customOptionResolvers: CustomOptionResolvers = form
        ? {
              listeSelectionDataResolver: createListeSelectionDataResolver(form),
          }
        : {};

    return (
        <Container maxWidth={containerWidth}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '32px',
                }}
            >
                <CurrentFormalityAndClient currentCatalogOnly />
            </Box>
            {page === 0 ? <Previous onClick={onPrevious} /> : null}
            {formjson ? (
                <ParcoursSirenRneProvider>
                    <JsonForm
                        onSkip={async (data: Record<string, unknown>) => {
                            setLoading(true);
                            await updateJsonPrestationFormalite(
                                prestationFormaliteApi,
                                presta && presta['@id'] ? castIriToId(presta['@id']) : null,
                                JSON.stringify(data),
                            );
                            if (dossier.id) {
                                refreshDossier(dossier.id);
                            }
                            navigate(parcoursFormalitePathResolver('stepDocs'));
                        }}
                        onChange={async (curr, newPagelenth, data) => {
                            if (curr === newPagelenth) {
                                if (onFinish) {
                                    onFinish(data);
                                } else if (collection.id) {
                                    setLoading(true);
                                    await updateJsonPrestationFormalite(
                                        prestationFormaliteApi,
                                        presta && presta['@id'] ? castIriToId(presta['@id']) : null,
                                        JSON.stringify(data),
                                    );
                                    const haveNext = isNextPresta();
                                    if (haveNext) {
                                        navigate(
                                            parcoursFormalitePathResolver(
                                                'stepForm',
                                                indexPresta + 1,
                                                0,
                                            ),
                                        );
                                    } else {
                                        if (dossier.id) {
                                            await refreshDossier(dossier.id);
                                        }
                                        navigate(parcoursFormalitePathResolver('stepDocs'));
                                    }
                                }
                            } else {
                                navigate(
                                    parcoursFormalitePathResolver('stepForm', indexPresta, curr),
                                );
                                setPagelenth(newPagelenth);
                            }
                        }}
                        visibility={currentCatalog?.offre?.visibilite || 'FULL'}
                        debugMode={false}
                        json={formjson as jsonForm}
                        startPage={page}
                        defaultValues={defaultValues}
                        customOptionResolvers={customOptionResolvers}
                        submitStyle={{
                            width: '100%',
                            marginTop: '30px',
                            padding: '15px',
                            fontSize: '1rem',
                            borderRadius: '15px',
                        }}
                    />
                </ParcoursSirenRneProvider>
            ) : (
                <Backdrop
                    sx={(thm) => ({ color: '#fff', zIndex: thm.zIndex.drawer + 1 })}
                    open
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
            )}
        </Container>
    );
};

export default StepFormulaires;
